import { GivingActivityDay } from "@shared/models";

/**
 * Derives the viewer's own calendar date from an instant, using the SAME `tzOffsetMinutes` the
 * request sent to the api -- never the machine's local getters (`getFullYear`/`getMonth`/`getDate`)
 * and never `toISOString().slice(0, 10)` (UTC, wrong for every negative offset after ~19:00 local).
 *
 * Reading local getters would also be UNFALSIFIABLE by a jest run on a UTC test machine: there, a
 * local-derived and a UTC-derived key are byte-identical, so no test could distinguish them
 * (overview-rebuild-pr5-plan.md's own falsifiability note). Shifting the instant by the offset and
 * reading UTC getters off the SHIFTED value is deterministic on any machine regardless of its own
 * zone, which is what makes the tests below actually able to fail.
 *
 * Sign convention, matching the api's own doc: `local = utc - tzOffsetMinutes` (`Date#getTimezoneOffset()`
 * convention -- positive west of Greenwich).
 *
 * @param now - the instant to derive today's date from.
 * @param tzOffsetMinutes - the SAME offset value sent as the request's `tzOffsetMinutes`, so the
 *   server's bucketing and this derivation agree by construction rather than by both reading the
 *   clock independently.
 * @returns the viewer's own calendar date, as `yyyy-MM-dd`.
 */
export const localDateKey = (now: Date, tzOffsetMinutes: number): string => {
  const shifted = new Date(now.getTime() - tzOffsetMinutes * 60_000);
  const year = shifted.getUTCFullYear();
  const month = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const day = String(shifted.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Which pill (if any) is today, matched on `days[].date` -- never on array position. The api plan's
 * own contract: if no date matches, NO pill is today. A viewer far enough east or west of the server's
 * assumptions can hold a local date the 7-day strip doesn't cover; a correct `tzOffsetMinutes` makes
 * this rare, not never. Callers must not fall back to index 0 or to `new Date()` when this returns -1
 * -- either would reintroduce the exact "today's dot always shows something" contradiction the api's
 * own superseded revision shipped.
 *
 * @param days - the 7-day strip, in server order.
 * @param now - the instant to resolve "today" against.
 * @param tzOffsetMinutes - the same offset value sent to the api (see `localDateKey`).
 * @returns the matching index, or -1 if no day in the strip is today.
 */
export const resolveTodayIndex = (days: GivingActivityDay[], now: Date, tzOffsetMinutes: number): number =>
  days.findIndex((day) => day.date === localDateKey(now, tzOffsetMinutes));

/**
 * The short weekday label for one day pill, e.g. `"Mon"`. Built from the date's OWN parts via
 * `Date.UTC` and read back with `{ timeZone: "UTC" }` -- never `new Date("2026-09-14")` (which the
 * spec defines as UTC midnight, but would render as the PREVIOUS weekday for every viewer west of
 * Greenwich if read back through local getters). `date` is a bare calendar date with no instant of its
 * own, so there is no timezone question to answer -- the safe move is to keep it out of local time
 * entirely, not to convert it "correctly". See this directory's plan doc for why mixing UTC
 * construction with local reading (or vice versa), not choosing either zone, is the actual hazard.
 *
 * @param date - a `yyyy-MM-dd` calendar date, as sent by the api.
 * @returns the three-letter weekday label, e.g. `"Mon"`.
 */
export const dayLabel = (date: string): string => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    weekday: "short",
    timeZone: "UTC",
  });
};

/**
 * The full weekday name for one day pill's dot, e.g. `"Monday"` -- review fix C3: the dot carries its
 * state by colour alone, so a screen reader needs a real accessible name, and the pill's own visible
 * label (`dayLabel`, three letters) reads worse as a spoken word than the full name does. Same
 * UTC-by-construction rule as `dayLabel` above, for the same reason -- see that function's own comment.
 *
 * @param date - a `yyyy-MM-dd` calendar date, as sent by the api.
 * @returns the full weekday name, e.g. `"Monday"`.
 */
export const dayFullLabel = (date: string): string => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });
};

/** The three states a day pill's dot can render as -- never a colour string (the component decides
 * the actual colour/class from this). */
export type DayPillState = "given" | "today-open" | "empty";

/**
 * A day pill's dot state. `total > 0` is checked BEFORE `isToday` -- pavel's decision (this plan's own
 * "Decisions of record"): a day you gave on is `"given"` even when it is today, because the ring on
 * the pill already marks "today", and the dot's job is only ever "did you give". Checking `isToday`
 * first would silently implement the rejected alternative (today's dot always reading as open/white
 * regardless of whether anything was given).
 *
 * @param day - the day's server-sent total.
 * @param isToday - whether this is the pill `resolveTodayIndex` matched, if any.
 * @returns `"given"` (total > 0), `"today-open"` (today, nothing given yet), or `"empty"`.
 */
export const dayPillState = (day: GivingActivityDay, isToday: boolean): DayPillState => {
  if (day.total > 0) return "given";
  if (isToday) return "today-open";
  return "empty";
};
